import React, { useEffect, useState, useCallback } from 'react';
import { COLOR_PRIMARY_DARK, COLOR_PRIMARY_LIGHT, COLOR_F } from 'src/const';

import { Grid, Modal, Button, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles, createMuiTheme } from '@material-ui/core/styles';
import { grey } from '@material-ui/core/colors';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CircularProgress from '@material-ui/core/CircularProgress';
import Skeleton from '@material-ui/lab/Skeleton';

import { Login } from '../../../../Pages/Login';
import { Register } from '../../../../Pages/Register';

import { DataGridCard } from '../../../../HOC/Cards';
import { GridColDef } from '@mui/x-data-grid';

import axios from 'axios';
import { Helmet } from 'react-helmet';

const useStyles = makeStyles(theme => ({
  adminContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#E8EDF0',
    paddingBottom: 50,
  },
  signupModal: {
    position: 'absolute',
    [theme.breakpoints.up('xs')]: {
      width: 340,
    },
    [theme.breakpoints.up('sm')]: {
      width: 530,
    },
    [theme.breakpoints.up('lg')]: {
      width: 650,
    },
    height: 550,
    backgroundColor: grey[200],
    //border: '2px solid #000',
    //boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  controlButtonsContainer: {
    paddingTop: 40,
    paddingBottom: 40,
  },
  controlButtons: {
    backgroundColor: COLOR_PRIMARY_DARK,
    color: '#ffffff',
    margin: '0 2vw',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
  },
  updateDataContainer: {
    backgroundColor: '#ffffff',
    width: '50vw',
    height: 500,
    borderColor: '#000000',
    marginBottom: 50,
  },
  uploadStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  visualizeDataContainer: {
    width: '95%',
    display: 'flex',
    flexDirection: 'column',
  },
  tagInput: {
    width: 200,
    marginBottom: 30,
  },
}));

const theme = createMuiTheme({
  palette: {
    primary: {
      main: COLOR_F,
    },
  },
});

interface ControlStateInterface {
  showUpdateData: boolean;
  showViualizeData: boolean;
  showTrackStatus: boolean;
}

const IBAS = ({ getTokenThunk, getUserInfo }) => {
  const canonicalURL = `http://app.airbtics.com/admin/ibas`;
  const classes = useStyles();

  useEffect(() => {
    getUserInfo();
  }, [getUserInfo]);

  const isLoggedIn = Boolean(getTokenThunk());
  const [signupPage, setSignupPage] = useState<boolean>(true);
  const redirectToLoginPage = () => {
    setSignupPage(false);
  };
  const redirectToSignupPage = () => {
    setSignupPage(true);
  };

  const [controlState, setControlState] = useState<ControlStateInterface>({
    showUpdateData: true,
    showViualizeData: false,
    showTrackStatus: false,
  });

  const handleControlClick = (event: 'update' | 'visualize' | 'track') => {
    let emptyState: ControlStateInterface = {
      showUpdateData: false,
      showViualizeData: false,
      showTrackStatus: false,
    };

    if (event === 'update') {
      emptyState['showUpdateData'] = true;
    }
    if (event === 'visualize') {
      emptyState['showViualizeData'] = true;
      visualizeData();
    }
    if (event === 'track') {
      emptyState['showTrackStatus'] = true;
    }
    setControlState(emptyState);
  };

  const [newFile, setNewFile] = useState<File>(new File([''], 'empty_file.csv'));

  const selectFile = (csvFile: File) => {
    setNewFile(csvFile);
  };

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  const [loading, setLoading] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [visualizeSuccess, setVisualizeSuccess] = useState<boolean>(false);
  const [checkStatusSuccess, setCheckStatusSuccess] = useState<boolean>(false);

  const [csvCols, setCSVCols] = useState<number>(0);
  const [failedRows, setFailedRows] = useState([]);
  const [unmatchedListings, setUnmatchedListings] = useState([]);
  const [dailyScrapedListingsData, setDailyScrapedListingsData] = useState([]);
  const [tag, setTag] = useState('');
  const [tagScrapingProgress, setTagScrapingProgress] = useState([]);
  const [overallDailyProgress, setOverallDailyProgress] = useState([]);
  const [overallWeeklyProgress, setOverallWeeklyProgress] = useState([]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      selectFile(event.target.files[0]);
    }
  };

  let failedRowsCols: GridColDef[] = [];

  const uploadFile = async () => {
    try {
      setLoading(true);
      let encodedFile: any = await toBase64(newFile);
      encodedFile.replace('data:application/vnd.ms-excel;base64,', '');
      console.log('encoded file', encodedFile);

      const uploadLambdaURL = `https://jctj5tbx8k.execute-api.us-east-2.amazonaws.com/default/addDailyScrapingListingIDs?file=${encodedFile}`;
      const response = await axios.get(uploadLambdaURL);

      setCSVCols(response.data.message.CSV_cols);
      setFailedRows(response.data.message.failed_rows);
      setUnmatchedListings(response.data.message.unmatched_listings);

      setLoading(false);
      setUploadSuccess(true);

      failedRowsCols =
        csvCols == 2
          ? [
              { field: 'listingID', headerName: 'Listing ID', width: 200 },
              { field: 'tag', headerName: 'Tag', flex: 1 },
            ]
          : [
              { field: 'district', headerName: 'District', width: 200 },
              { field: 'city', headerName: 'City', flex: 3 },
              { field: 'country_code', headerName: 'Country Code', flex: 2 },
              { field: 'bedrooms', headerName: 'Bedrooms', flex: 1 },
              { field: 'pool', headerName: 'Pool', flex: 1 },
              { field: 'tag', headerName: 'Tag', flex: 2 },
            ];
    } catch (error) {
      console.log('Unhandled error while calling API ', error);
    }
  };

  const visualizeData = async () => {
    try {
      const trackLambdaURL =
        'https://pst380misd.execute-api.us-east-2.amazonaws.com/default/visualizeDailyScrapingListingIDs';
      const response = await axios.get(trackLambdaURL);

      setDailyScrapedListingsData(response.data.message.visualized_data);
      setVisualizeSuccess(true);
    } catch (error) {
      console.log('Unhandled error while calling API ', error);
    }
  };

  const handleTagChange = useCallback(event => {
    setTag(event.target.value);
  }, []);

  const checkStatus = async () => {
    try {
      const trackLambdaURL = `https://5t50lr3uhb.execute-api.us-east-2.amazonaws.com/default/getDailyScrapingProgress?tag=${tag}`;
      const response = await axios.get(trackLambdaURL);

      setTagScrapingProgress(response.data.message.tag_scraping_progress);
      setOverallDailyProgress(response.data.message.overall_daily_progress);
      setOverallWeeklyProgress(response.data.message.overall_weekly_progress);
      setCheckStatusSuccess(true);
    } catch (error) {
      console.log('Unhandled error while calling API ', error);
    }
  };

  const unmatchedListingsCols: GridColDef[] = [
    { field: 'listingID', headerName: 'Listing ID', width: 200 },
    { field: 'tag', headerName: 'Tag', flex: 1 },
  ];

  const dailyScrapedListingsCols: GridColDef[] = [
    { field: 'listingID', headerName: 'Listing ID', width: 200 },
    { field: 'city', headerName: 'City', flex: 1 },
    { field: 'country_code', headerName: 'Country Code', flex: 1 },
  ];

  const scrapedStatusCols: GridColDef[] = [
    { field: 'date', headerName: 'Date', width: 200 },
    { field: 'progress', headerName: 'Progress', flex: 1 },
  ];

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>IBAS</title>
        <link rel="canonical" href={canonicalURL} />
      </Helmet>

      <Grid
        container
        direction="column"
        justify="flex-start"
        alignItems="center"
        className={classes.adminContainer}
      >
        <Grid
          container
          direction="row"
          justify="center"
          alignItems="center"
          className={classes.controlButtonsContainer}
        >
          <Button
            variant="contained"
            className={classes.controlButtons}
            onClick={() => {
              handleControlClick('update');
            }}
          >
            Update Data
          </Button>
          <Button
            variant="contained"
            className={classes.controlButtons}
            onClick={() => {
              handleControlClick('visualize');
            }}
          >
            Visualize Data
          </Button>
          <Button
            variant="contained"
            className={classes.controlButtons}
            onClick={() => {
              handleControlClick('track');
            }}
          >
            Track Status
          </Button>
        </Grid>

        {controlState.showUpdateData && (
          <>
            <Grid
              container
              direction="column"
              justify="center"
              alignItems="center"
              className={classes.updateDataContainer}
            >
              <input
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                id="csv-file-uploader"
                multiple
                type="file"
                onChange={handleFileChange}
                style={{ width: 200, marginBottom: 30 }}
              />
              <Button
                variant="contained"
                className={classes.controlButtons}
                startIcon={<CloudUploadIcon />}
                component="span"
                onClick={uploadFile}
                style={{ marginBottom: 50 }}
              >
                Upload
              </Button>

              {loading && <CircularProgress />}

              {uploadSuccess && <Alert severity="info">Upload Successfull!</Alert>}
            </Grid>

            {!uploadSuccess ? (
              <Skeleton variant="rect" animation="wave" width="100%" height={450} />
            ) : (
              <Grid
                container
                item
                xs={12}
                md={12}
                lg={12}
                spacing={4}
                className={classes.uploadStatusContainer}
              >
                {failedRows && (
                  <DataGridCard
                    cardTitle="Failed Rows"
                    rowData={failedRows}
                    colData={failedRowsCols}
                    height={600}
                  />
                )}

                {unmatchedListings && (
                  <DataGridCard
                    cardTitle="Unmatched Listings"
                    rowData={unmatchedListings}
                    colData={unmatchedListingsCols}
                    height={600}
                  />
                )}
              </Grid>
            )}
          </>
        )}

        {controlState.showViualizeData && (
          <>
            {!visualizeSuccess ? (
              <Skeleton variant="rect" animation="wave" width="100%" height={450} />
            ) : (
              <Grid className={classes.visualizeDataContainer}>
                {dailyScrapedListingsData && (
                  <div style={{ height: '100vh' }}>
                    <DataGridCard
                      cardTitle="Daily Scraping Table"
                      rowData={dailyScrapedListingsData}
                      colData={dailyScrapedListingsCols}
                      height={600}
                    />
                  </div>
                )}
              </Grid>
            )}
          </>
        )}

        {controlState.showTrackStatus && (
          <>
            <TextField
              onChange={handleTagChange}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="tag"
              label="Tag"
              name="tag"
              autoFocus
              className={classes.tagInput}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              className={classes.tagInput}
              onClick={() => {
                checkStatus();
              }}
            >
              Track
            </Button>

            {!checkStatusSuccess ? (
              <Skeleton variant="rect" animation="wave" width="100%" height={450} />
            ) : (
              <Grid
                container
                item
                xs={12}
                md={12}
                lg={12}
                spacing={4}
                className={classes.uploadStatusContainer}
              >
                {tagScrapingProgress && (
                  <DataGridCard
                    cardTitle="Tag Scraping Progress"
                    rowData={tagScrapingProgress}
                    colData={scrapedStatusCols}
                    height={600}
                  />
                )}

                {overallDailyProgress && (
                  <DataGridCard
                    cardTitle="Overall Daily Progress"
                    rowData={overallDailyProgress}
                    colData={scrapedStatusCols}
                    height={600}
                  />
                )}

                {overallWeeklyProgress && (
                  <DataGridCard
                    cardTitle="Overall Weekly Progress"
                    rowData={overallWeeklyProgress}
                    colData={scrapedStatusCols}
                    height={600}
                  />
                )}
              </Grid>
            )}
          </>
        )}

        {/* Login and signup popup */}
        <div>
          <Modal
            open={!isLoggedIn}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <Grid
              container
              spacing={0}
              direction="column"
              alignItems="center"
              justify="center"
              style={{ minHeight: '100vh' }}
            >
              <div className={`${classes.signupModal} modal-custom`}>
                {signupPage ? (
                  <Register redirectToLoginPage={redirectToLoginPage} />
                ) : (
                  <Login redirectToSignupPage={redirectToSignupPage} />
                )}
              </div>
            </Grid>
          </Modal>
        </div>
      </Grid>
    </>
  );
};

export default IBAS;
